import * as React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import moment from 'moment';
import { Box, Divider, Tooltip, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import { localizeDate } from '@pec/aion-ui-i18next/helpers/localize';
import { formats } from '@pec/aion-ui-i18next/constants';
import { certificationLogos } from '../../helpers/logos';
import { CertificationStatus, ICertification, VerificationCategory } from '../../interfaces/certifications';
import { usePermission } from '../../hooks/usePermission';
import { ActivityAction } from '@pec/aion-ui-core/interfaces/activities';
import { TFunction } from 'i18next';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    logo: {
      height: 68,
      width: 68
    },
    noLogo: {
      backgroundColor: '#D9D9D9'
    },
    name: {
      fontSize: 15,
      fontWeight: 500,
      lineHeight: '24px',
      letterSpacing: 0.5,
      color: 'var(--on-surface-high-emphasis-000000-87-)'
    },
    date: {
      fontSize: 13,
      lineHeight: '20px',
      letterSpacing: 0.25,
      color: 'var(--on-surface-medium-emphasis-000000-60-)',
      marginRight: theme.spacing(0.5)
    },
    link: {
      fontSize: 13,
      lineHeight: '20px',
      letterSpacing: 0.25,
      color: 'var(--primary-blue-six)',
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline'
      }
    },
    divider: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      height: '1rem',
      alignSelf: 'center',
      width: '1px',
      background: 'var(--on-surface-medium-emphasis-000000-60-)'
    },
    warningWrapper: {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: '#FCEEEE',
      borderRadius: 8,
      minHeight: 34,
      padding: theme.spacing(0.75, 2)
    },
    warningText: {
      fontSize: 12,
      fontWeight: 400,
      lineHeight: '16px',
      letterSpacing: 0.4,
      color: '#410E0B',
      marginLeft: theme.spacing(1.5)
    },
    infoIcon: {
      color: 'var(--on-surface-medium-emphasis-000000-60-)'
    },
    warningIcon: {
      color: '#FF0000'
    }
  })
);

export const TestLocators = {
  logo: 'logo',
  name: 'name',
  date: 'date',
  link: 'link',
  warning: 'warning',
  refresh: 'refresh'
};

const makeContent = (t: TFunction) => ({
  assured: t('newDashboard.certifications.assured', 'Assured'),
  complete: t('newDashboard.certifications.complete', 'Complete'),
  validUntil: t('newDashboard.certifications.validUntil', 'Valid until'),
  expired: t('newDashboard.certifications.validUntil', 'Expired'),
  tooltip: t('newDashboard.certifications.tooltip', 'Certificate is only valid with an active membership.'),
  download: t('newDashboard.certifications.download', 'Download certificate.'),
  regenerateTooltip: t(
    'newDashboard.certifications.regenerate',
    'Click to regenerate the certificate. This will replace the current certificate with a new one.'
  ),
  regenerate: t('newDashboard.certifications.regenerate', 'Refresh.'),
  warningMessage: t('newDashboard.certifications.warning', 'Certificate is only valid with an active membership.'),
  incompleteStatusWarning: t(
    'newDashboard.certifications.incompleteStatusWarningMessage',
    'Your certificate will appear here when your assessments have been marked as compliant.'
  )
});

type Props = {
  certification: ICertification;
  shouldHideRefresh: boolean;
  onDownloadClick: () => void;
  onRefreshClick: (accreditationId: string) => void;
};
export const Certification: React.FC<Props> = ({
  certification,
  shouldHideRefresh,
  onDownloadClick,
  onRefreshClick
}) => {
  const classes = useStyles();
  const { t } = useTranslation('subscriber');
  const content = makeContent(t);
  const { hasDocumentsPermission } = usePermission();
  const { hasAccreditationsPermission } = usePermission(ActivityAction.Manage);
  const {
    name,
    status,
    validToDateTimeUtc,
    verificationCategory,
    isExpired,
    isCurrentMembershipExpired,
    isSubscriptionDeleted,
    accreditationTypeId
  } = certification;
  const infoIconVisible =
    !isExpired &&
    !isCurrentMembershipExpired &&
    ![CertificationStatus.Expired, CertificationStatus.Incomplete].includes(status);
  const certificateIsIncomplete = status === CertificationStatus.Incomplete;
  const Logo = certificationLogos[accreditationTypeId];

  const warningText = (message: string) => {
    return (
      <>
        <Box className={classes.warningWrapper} data-testid={TestLocators.warning}>
          <ReportProblemOutlinedIcon className={classes.warningIcon} fontSize="small" />
          <Typography classes={{ root: classes.warningText }}>{message}</Typography>
        </Box>
      </>
    );
  };

  return (
    <Box display="flex" paddingTop={2.5}>
      {Logo ? (
        <Logo className={classes.logo} data-testid={TestLocators.logo} />
      ) : (
        <Box className={clsx(classes.noLogo, classes.logo)} />
      )}
      <Box marginLeft={2}>
        <Typography classes={{ root: classes.name }} data-testid={TestLocators.name}>
          {name}
          {verificationCategory === VerificationCategory.Assured ? ` ${content.assured}` : ''}:{' '}
          {status === CertificationStatus.Inactive || status === CertificationStatus.Active ? content.complete : status}
        </Typography>
        {status !== CertificationStatus.Incomplete && (
          <>
            <Box display="flex">
              <Typography classes={{ root: classes.date }} data-testid={TestLocators.date}>
                {status === CertificationStatus.Active ? content.validUntil : content.expired}{' '}
                {localizeDate(
                  {
                    value: moment(`${moment(validToDateTimeUtc).format('YYYY-MM-DD')}T00:00:00`).toISOString(),
                    config: { day: '2-digit', month: '2-digit', year: 'numeric' }
                  },
                  t,
                  formats.dateCustom
                )}
              </Typography>
              {infoIconVisible && (
                <Tooltip placement="bottom-start" title={content.tooltip.toString()}>
                  <InfoOutlinedIcon className={classes.infoIcon} fontSize="small" />
                </Tooltip>
              )}
            </Box>
            <Box display={'flex'}>
              {hasDocumentsPermission && (
                <Typography classes={{ root: classes.link }} onClick={onDownloadClick} data-testid={TestLocators.link}>
                  {content.download}
                </Typography>
              )}
              {hasAccreditationsPermission && !isSubscriptionDeleted && !shouldHideRefresh && (
                <Box display="flex">
                  <Divider className={classes.divider} orientation="vertical" variant="middle" flexItem />
                  <Tooltip placement="right" title={content.regenerateTooltip as string}>
                    <Typography
                      classes={{ root: classes.link }}
                      onClick={() => onRefreshClick(certification.id)}
                      data-testid={TestLocators.refresh}
                    >
                      {content.regenerate}
                    </Typography>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </>
        )}
        {(isExpired || isCurrentMembershipExpired) && warningText(content.warningMessage)}
        {certificateIsIncomplete && !isExpired && warningText(content.incompleteStatusWarning)}
      </Box>
    </Box>
  );
};
